import { LoadingButton } from '@mui/lab'
import { Box, Button, Card, CardActions, CardContent, CardHeader, Dialog, DialogContent, Divider, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getImageURL } from 'src/_utils'
import { MapitFileInput } from 'src/services/shared/components'

export default function UploadIdPhotoCard({
    idDocument,
    loading,
    isSuccess,
    error,
    onConfirm,
}) {
    const { t } = useTranslation()
    const [openImage, setOpenImage] = useState(false)

    const fileInputRef = React.useRef(null)

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const [uploadedFile, setUploadedFile] = useState('')
    const [fileError, setFileError] = useState('')

    const handleFileChange = ({ target: { value } }) => {
        setUploadedFile(value)
        setFileError('')
    }

    const handleFileError = (error) => {
        setFileError(error)
        setUploadedFile(null)
    }

    const document = useMemo(() => {
        if (!!uploadedFile) return uploadedFile
        if (!!idDocument?.name) return getImageURL({ image: idDocument })
        return null

    }, [idDocument, uploadedFile])

    const isPdf = useMemo(() => {
        if (!document) return false
        if (document.startsWith('data:application/pdf')) return true
        if (typeof document === 'string' && document.toLowerCase().endsWith('.pdf')) return true
        return false
    }, [document])

    return (
        <>
            {!!document &&
                <Dialog maxWidth="lg" fullWidth onClose={() => setOpenImage(false)} open={openImage}>
                    {isPdf ? (
                        <DialogContent sx={{ height: '80vh', p: 0 }}>
                            <iframe src={document} width="100%" height="100%" style={{ border: 'none' }} title="PDF preview" />
                        </DialogContent>
                    ) : (
                        <img src={document} style={{ objectFit: 'contain' }} alt={`upload`} />
                    )}
                </Dialog>
            }
            <Card>
                <CardHeader title={t("Photo of ID / Residency Permit")} />
                <Divider />
                <CardContent>
                    <Card elevation={!document ? 0 : undefined}>
                        {
                            document ?
                                isPdf ? (
                                    <Box
                                        onClick={() => setOpenImage(true)}
                                        sx={{
                                            height: 180,
                                            width: "100%",
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            bgcolor: 'grey.100',
                                        }}
                                    >
                                        <Typography variant="h6" color="text.secondary">
                                            PDF
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box
                                        component="img"
                                        onClick={() => setOpenImage(true)}
                                        sx={{
                                            maxHeight: 180,
                                            width: "100%",
                                            objectFit: "cover",
                                            cursor: 'pointer',
                                        }}
                                        alt="photo of ID / residency permit"
                                        src={document}
                                    />
                                )
                                :
                                <Box display="flex" alignItems="center" justifyContent="center" height={200} width="100%">
                                    <Typography>
                                        {t("Please upload the photo of your ID or residency permit.")}
                                    </Typography>
                                </Box>
                        }
                        <CardActions>
                            <MapitFileInput
                                ref={fileInputRef}
                                name="transactionFile"
                                accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf"
                                maxSize={3}
                                onChange={handleFileChange}
                                onError={handleFileError}
                            />
                            <Button
                                variant="text"
                                color="primary"
                                fullWidth
                                sx={{ textTransform: 'none' }}
                                onClick={triggerFileInput}
                            >
                                {t("Photo of ID / Residency Permit")}
                            </Button>

                        </CardActions>
                    </Card>
                </CardContent>
                <Divider />
                <CardActions>
                    {(!!error || !!fileError) && <Typography color='text.danger'>{error ?? fileError}</Typography>}
                    {isSuccess && <Typography color='success.main'>{t("Done successfully")}</Typography>}
                    <Box mr="auto" />
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={() => onConfirm({ idDocument: uploadedFile })}
                        loading={loading}
                        disabled={loading || !uploadedFile}
                        sx={{ textTransform: 'none' }}
                    >
                        {t("Update")}
                    </LoadingButton>
                </CardActions>
            </Card>
        </>
    )
}
